import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useT } from '@/i18n/LocaleContext';
import { Activity, ArrowRight } from 'lucide-react';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { Section } from '@/components/site/Section';
import { Eyebrow } from '@/components/site/Eyebrow';
import { Pillar } from '@/components/site/Pillar';
import { SiteImage } from '@/components/site/SiteImage';
import { GridPattern } from '@/components/site/GridPattern';
import { GlowOrb } from '@/components/site/GlowOrb';

export default function Home() {
  const t = useT('site_home');
  const { localizedTo } = useLocalizedNavigate();
  // DEBUG SSG:

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <Section variant="darker" padding="xl" className="relative">
        <GridPattern intensity="strong" />
        <GlowOrb color="cyan" size="xl" className="-top-32 -right-32 opacity-70" />
        <GlowOrb color="blend" size="lg" className="-bottom-32 -left-32 opacity-60" />

        <div className="relative z-10 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Eyebrow icon={Activity}>{t('hero.eyebrow')}</Eyebrow>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] text-white md:text-6xl lg:text-7xl">
              {t('hero.titleLine1')}
              <br />
              <span className="text-gradient-vyxora">{t('hero.titleLine2')}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-vyxora-text-light/80">
              {t('hero.subcopy')}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={localizedTo('/plataforma')} className="btn-primary">
                {t('hero.ctaPrimary')} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={localizedTo('/cases')} className="btn-secondary">
                {t('hero.ctaSecondary')}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="relative"
          >
            <SiteImage
              src="/imagens/site/hero-vyxora.jpg"
              alt="VYXORA"
              aspect="16/9"
              priority
              glow
              maskWatermark
            />
          </motion.div>
        </div>
      </Section>

      {/* PROBLEMA */}
      <Section variant="dark" padding="lg" bordered>
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div>
            <Eyebrow>{t('problema.eyebrow')}</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-bold leading-tight text-white md:text-5xl">
              {t('problema.title')}
            </h2>
            <p className="mt-6 text-lg text-vyxora-text-light/75">
              {t('problema.subcopy')}
            </p>

            <ul className="mt-10 space-y-6">
              {(['item1', 'item2', 'item3'] as const).map((k) => (
                <li key={k} className="border-l-2 border-vyxora-cyan-400/40 pl-4">
                  <h3 className="font-display text-lg font-semibold text-white">
                    {t(`problema.${k}.title`)}
                  </h3>
                  <p className="mt-1 text-sm text-vyxora-text-light/70">
                    {t(`problema.${k}.desc`)}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <SiteImage
            src="/imagens/site/problema-monitores.jpg"
            alt="Múltiplos monitores no centro cirúrgico"
            aspect="16/9"
            glow
            maskWatermark
          />
        </div>
      </Section>

      {/* SALA EXPANDIDA — VY/X/ORA */}
      <Section variant="darker" padding="lg" className="relative">
        <GridPattern intensity="subtle" />

        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>{t('salaExpandida.eyebrow')}</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight text-white md:text-5xl">
            {t('salaExpandida.title')}
          </h2>
          <p className="mt-4 text-lg text-vyxora-text-light/75">
            {t('salaExpandida.subcopy')}
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {(['vy', 'x', 'ora'] as const).map((k) => (
            <Pillar
              key={k}
              letter={t(`salaExpandida.${k}.letter`)}
              eyebrow={t(`salaExpandida.${k}.name`)}
              title={t(`salaExpandida.${k}.name`)}
              desc={t(`salaExpandida.${k}.desc`)}
            />
          ))}
        </div>

        <div className="mt-16 mx-auto max-w-5xl">
          <SiteImage
            src="/imagens/site/sala-expandida.jpg"
            alt="Sala de cirurgia expandida"
            aspect="16/9"
            glow
            maskWatermark
          />
        </div>
      </Section>

      {/* PROVA SOCIAL */}
      <Section variant="dark" padding="md" bordered>
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>{t('provaSocial.eyebrow')}</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">
            {t('provaSocial.title')}
          </h2>
          <p className="mt-4 text-vyxora-text-light/75">{t('provaSocial.subcopy')}</p>
          <Link
            to={localizedTo('/cases')}
            className="mt-8 inline-flex items-center gap-2 text-vyxora-cyan-300 hover:text-vyxora-cyan-400"
          >
            {t('provaSocial.cta')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      {/* CTA FINAL */}
      <Section variant="accent" padding="lg" className="text-vyxora-abyss">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">
            {t('ctaFinal.title')}
          </h2>
          <p className="mt-4 text-vyxora-abyss/80">{t('ctaFinal.subcopy')}</p>
          <Link
            to={localizedTo('/contato')}
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-vyxora-abyss px-8 py-3 font-semibold text-vyxora-cyan-300 transition hover:bg-black"
          >
            {t('ctaFinal.cta')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
